/**
 * The scrollable message thread of one chat room — shared by the Public
 * room and Support tabs. Reads the room from chatSlice (history pages and
 * live frames are already merged there); the owning component decides when
 * the room is held and synced (`useHeldRoom`).
 *
 * Own messages (sender = the viewer's `sub`) sit right in the accent colour
 * labelled "You"; everyone else's sit left. Scrolling follows new messages
 * only while the reader is at the bottom or just sent one, and holds its
 * place when an older page is prepended.
 */
import { useLayoutEffect, useRef } from "react";
import { api } from "@/api/client";
import type { ChatMessage } from "@/api/types";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchChatHistory } from "@/state/slices/chatSlice";
import styles from "./ChatRoom.module.css";

// Within this many px of the bottom counts as "reading the latest".
const STICK_TO_BOTTOM_PX = 48;
const NO_MESSAGES: ChatMessage[] = [];

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
}

function MessageRow({
  message,
  mine,
  othersLabel,
  senderLabel,
}: {
  message: ChatMessage;
  mine: boolean;
  othersLabel?: string;
  senderLabel?: (name: string, message: ChatMessage) => string;
}) {
  const name = message.sender_name || "Unknown user";
  const who = mine
    ? "You"
    : senderLabel
      ? senderLabel(name, message)
      : othersLabel
        ? `${name} · ${othersLabel}`
        : name;
  const time = formatTime(message.created_at);
  return (
    <li className={`${styles.message} ${mine ? styles.mine : styles.theirs}`} data-mine={mine}>
      <div className={styles.meta}>
        {who}
        {time ? ` · ${time}` : ""}
      </div>
      <div className={styles.bubble}>
        {message.message_type === "image" ? (
          <img
            className={styles.image}
            src={api.chatImageUrl(message.content)}
            alt={message.original_filename ?? "Image"}
          />
        ) : (
          message.content
        )}
      </div>
    </li>
  );
}

export function ChatThread({
  roomId,
  emptyText,
  othersLabel,
  senderLabel,
}: {
  roomId: string;
  /** Shown once history has loaded and the room is empty. */
  emptyText: string;
  /** Appended to other senders' names, e.g. "Support" in the user's ticket. */
  othersLabel?: string;
  /** Other senders' label, per message (overrides `othersLabel`); `name`
   *  is the sender's display name with its fallback applied. */
  senderLabel?: (name: string, message: ChatMessage) => string;
}) {
  const dispatch = useAppDispatch();
  const room = useAppSelector((s) => s.chat.rooms[roomId]);
  const viewerSub = useAppSelector((s) => s.me.sub);
  const messages = room?.messages ?? NO_MESSAGES;

  const threadRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const olderAnchorRef = useRef<number | null>(null);

  const last = messages.length ? messages[messages.length - 1] : null;
  const lastId = last?.id ?? null;
  const lastIsMine = !!last && !!viewerSub && last.sender_id === viewerSub;
  const firstId = messages.length ? messages[0].id : null;

  useLayoutEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    if (olderAnchorRef.current !== null) {
      el.scrollTop += el.scrollHeight - olderAnchorRef.current;
      olderAnchorRef.current = null;
      return;
    }
    if (stickToBottomRef.current || lastIsMine) {
      el.scrollTop = el.scrollHeight;
      stickToBottomRef.current = true;
    }
  }, [lastId, firstId, lastIsMine]);

  const onScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_TO_BOTTOM_PX;
  };

  const loadOlder = () => {
    if (room?.olderCursor == null || room.loadingOlder) return;
    olderAnchorRef.current = threadRef.current?.scrollHeight ?? null;
    void dispatch(fetchChatHistory({ roomId, beforeId: room.olderCursor }));
  };

  return (
    <div className={styles.thread} ref={threadRef} onScroll={onScroll}>
      {room?.olderCursor != null ? (
        <button
          type="button"
          className={styles.loadOlder}
          onClick={loadOlder}
          disabled={room.loadingOlder}
        >
          {room.loadingOlder ? "Loading…" : "Load older messages"}
        </button>
      ) : null}

      {room?.status === "loading" && messages.length === 0 ? (
        <p className={styles.threadNote}>Loading messages…</p>
      ) : null}
      {room?.status === "failed" ? (
        <p className={styles.error} role="alert">
          {room.error ?? "Couldn't load messages."}{" "}
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => void dispatch(fetchChatHistory({ roomId }))}
          >
            Retry
          </button>
        </p>
      ) : null}
      {room?.status === "succeeded" && messages.length === 0 ? (
        <p className={styles.threadNote}>{emptyText}</p>
      ) : null}

      <ul className={styles.messages} aria-label="Messages">
        {messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            mine={!!viewerSub && m.sender_id === viewerSub}
            othersLabel={othersLabel}
            senderLabel={senderLabel}
          />
        ))}
      </ul>
    </div>
  );
}
